import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem("rcn_user");
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    });
    const [loading, setLoading] = useState(true);

    // Runs once on mount — verifies the persisted token and rehydrates user.
    // Intentionally has no deps (localStorage/api are stable module-level singletons).
    useEffect(() => {
        const token = localStorage.getItem("rcn_token");
        if (!token) { setLoading(false); return; }
        api.get("/auth/me")
            .then((r) => {
                setUser(r.data);
                localStorage.setItem("rcn_user", JSON.stringify(r.data));
            })
            .catch(() => {
                localStorage.removeItem("rcn_token");
                localStorage.removeItem("rcn_user");
                setUser(null);
            })
            .finally(() => setLoading(false));
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("rcn_token", data.access_token);
        localStorage.setItem("rcn_user", JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("rcn_token");
        localStorage.removeItem("rcn_user");
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, logout }),
        [user, loading, login, logout],
    );

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
